<template>
  <div v-if="hasValidData">
    <apexchart
      ref="chart"
      type="pie"
      :height="height"
      :options="chartOptions"
      :series="validSeries"
    />
  </div>
  <div v-else class="no-data-message">
    <p>Nenhum dado disponível</p>
  </div>
</template>

<script>
import VueApexCharts from "vue-apexcharts";
import { colors, commonOptions } from "./commons";

const percentFormatter = new Intl.NumberFormat("pt-BR", {
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});

function formatPercent(value) {
  if (!isFinite(value)) return "0,00%";
  return `${percentFormatter.format(value)}%`;
}

function computeTotal(series) {
  if (!Array.isArray(series)) return 0;
  return series.reduce(
    (sum, v) => sum + (typeof v === "number" && !isNaN(v) ? v : 0),
    0
  );
}

export default {
  name: "ModernPieChart",
  components: {
    apexchart: VueApexCharts,
  },
  props: {
    title: {
      type: String,
      default: "",
    },
    series: {
      type: Array,
      default: () => [],
    },
    labels: {
      type: Array,
      default: () => [],
    },
    colors: {
      type: Array,
      default: null,
    },
    height: {
      type: [String, Number],
      default: 300,
    },
    showDonut: {
      type: Boolean,
      default: false,
    },
    showDataLabels: {
      type: Boolean,
      default: true,
    },
    showLegend: {
      type: Boolean,
      default: true,
    },
    displayMode: {
      type: String,
      default: "absolute",
      validator: (v) => ["absolute", "percent"].includes(v),
    },
  },
  data() {
    const vm = this;
    return {
      chartOptions: commonOptions.apply({
        title: {
          text: this.title,
        },
        labels: this.labels,
        plotOptions: {
          pie: {
            startAngle: 0,
            endAngle: 360,
            expandOnClick: true,
            offsetX: 0,
            offsetY: 0,
            customScale: 1,
            dataLabels: {
              offset: 0,
              minAngleToShowLabel: 10,
            },
            donut: {
              size: this.showDonut ? "65%" : "0%",
              background: "transparent",
              labels: {
                show: false,
                name: {
                  show: true,
                  fontSize: "22px",
                  fontFamily: "'Inter', 'Helvetica Neue', sans-serif",
                  fontWeight: "600",
                  color: "#1e293b",
                  offsetY: -10,
                },
                value: {
                  show: true,
                  fontSize: "16px",
                  fontFamily: "'Inter', 'Helvetica Neue', sans-serif",
                  fontWeight: "400",
                  color: "#64748b",
                  offsetY: 16,
                  formatter: function (val) {
                    return val;
                  },
                },
                total: {
                  show: false,
                  label: "Total",
                  fontSize: "16px",
                  fontFamily: "'Inter', 'Helvetica Neue', sans-serif",
                  fontWeight: "600",
                  color: "#1e293b",
                  formatter: function (w) {
                    return w.globals.seriesTotals.reduce((a, b) => a + b, 0);
                  },
                },
              },
            },
          },
        },
        dataLabels: {
          enabled: this.showDataLabels,
          formatter: function (val, opts) {
            const absolute = opts.w.config.series[opts.seriesIndex];
            if (vm.displayMode === "percent") {
              return formatPercent(val);
            }
            return absolute;
          },
          textAnchor: "middle",
          distributed: false,
          offsetX: 0,
          offsetY: 0,
          style: {
            fontSize: "12px",
            fontFamily: "'Inter', 'Helvetica Neue', sans-serif",
            fontWeight: "600",
            colors: ["#ffffff"],
          },
          background: {
            enabled: true,
            foreColor: "#1e293b",
            borderRadius: 4,
            padding: 4,
            borderWidth: 0,
            opacity: 0.9,
            dropShadow: {
              enabled: false,
            },
          },
          dropShadow: {
            enabled: false,
          },
        },
        tooltip: {
          y: {
            formatter: function (val, opts) {
              const total = computeTotal(opts.w.config.series);
              const pct = total > 0 ? (val / total) * 100 : 0;
              return `${val} (${formatPercent(pct)})`;
            },
          },
        },
        legend: {
          show: this.showLegend,
          position: "bottom",
          horizontalAlign: "center",
          fontSize: "12px",
          fontFamily: "'Inter', 'Helvetica Neue', sans-serif",
          fontWeight: "500",
          markers: {
            width: 12,
            height: 12,
            radius: 6,
          },
          itemMargin: {
            horizontal: 10,
            vertical: 5,
          },
        },
        colors: this.colors || colors.default(),
      }),
    };
  },
  computed: {
    hasValidData() {
      return (
        Array.isArray(this.series) &&
        this.series.length > 0 &&
        this.series.some((s) => typeof s === "number" && !isNaN(s) && s > 0) &&
        Array.isArray(this.labels) &&
        this.labels.length > 0 &&
        this.labels.length === this.series.length
      );
    },
    validSeries() {
      if (!Array.isArray(this.series)) return [];
      return this.series.filter((s) => typeof s === "number" && !isNaN(s));
    },
  },
  watch: {
    series: {
      handler() {
        this.updateChart();
      },
      deep: true,
    },
    labels: {
      handler(newLabels) {
        this.chartOptions = {
          ...this.chartOptions,
          labels: newLabels,
        };
        this.updateChart();
      },
      deep: true,
    },
    colors: {
      handler(newColors) {
        this.chartOptions = {
          ...this.chartOptions,
          colors: newColors || colors.default(),
        };
        this.updateChart();
      },
      deep: true,
    },
    displayMode() {
      this.updateChart();
    },
  },
  methods: {
    updateChart() {
      if (this.$refs.chart) {
        const validSeries =
          Array.isArray(this.series) && this.series.length > 0
            ? this.series.filter((s) => typeof s === "number" && !isNaN(s))
            : [];

        const validLabels =
          Array.isArray(this.labels) && this.labels.length > 0
            ? this.labels.filter((l) => l !== null && l !== undefined)
            : [];

        if (validSeries.length === 0 || validLabels.length === 0) {
          return;
        }

        const minLength = Math.min(validSeries.length, validLabels.length);
        const finalSeries = validSeries.slice(0, minLength);
        const finalLabels = validLabels.slice(0, minLength);

        this.$refs.chart.updateOptions(
          {
            series: finalSeries,
            labels: finalLabels,
            colors: this.colors || colors.default(),
          },
          false,
          true
        );
      }
    },
  },
};
</script>
<style scoped>
.no-data-message {
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 200px;
  color: #64748b;
  font-family: "Inter", "Helvetica Neue", sans-serif;
  font-size: 14px;
}
</style>
